import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  MessageSquare, Send, ArrowLeft, User, Clock, Search, Filter, X, Camera, Shield, ZoomIn, Download
} from 'lucide-react';
import { messagesAPI, propertyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useMessagingSocket } from '../hooks/useMessagingSocket';
import EmojiPicker from './EmojiPicker';
import LivePhotoCapture from './LivePhotoCapture';

const MessagingPanel = ({ userRole = 'host', autoSelectConversationId = null, onUnreadChange = null }) => {
  const { toast } = useToast();
  const { getAuthToken, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // conversationId -> { senderName }
  const [showCamera, setShowCamera] = useState(false);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const messagesEndRef = useRef(null);
  const hasAutoSelected = useRef(false);
  const selectedConvRef = useRef(null);

  // Keep ref in sync for WebSocket callbacks
  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);

  const textareaRef = useRef(null);

  // Real-time WebSocket — single connection
  const handleNewMessage = useCallback((conversationId, message) => {
    // Add to messages if viewing this conversation
    if (selectedConvRef.current?._id === conversationId) {
      setMessages(prev => {
        if (prev.find(m => m._id === message._id)) return prev;
        return [...prev, message];
      });
    }
    // Update conversation list preview
    setConversations(prev => prev.map(conv =>
      conv._id === conversationId
        ? { ...conv, lastMessage: message.content?.substring(0, 100), lastMessageAt: message.createdAt }
        : conv
    ));
    // Notify parent about unread change (new message arrived)
    if (onUnreadChange) onUnreadChange();
  }, [onUnreadChange]);

  const handleTyping = useCallback((conversationId, senderId, senderName, isTyping) => {
    setTypingUsers(prev => {
      if (isTyping) {
        return { ...prev, [conversationId]: { senderName, senderId } };
      }
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
  }, []);

  const { isConnected, sendTypingIndicator } = useMessagingSocket({
    onNewMessage: handleNewMessage,
    onTyping: handleTyping,
    activeConversationId: selectedConversation?._id
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterTripStage, setFilterTripStage] = useState('all'); // all, upcoming, in-stay, completed
  const [filterProperty, setFilterProperty] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all'); // all, guests, landlords, vendors
  const [properties, setProperties] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversations(token, userRole);
      // Filter OUT service/restaurant conversations — those belong in Services > Messages tab
      // In guest mode: also strip deal conversations — guests only see their booking conversations
      let propertyConvs = data.filter(c =>
        !['service', 'delivery', 'ride', 'restaurant'].includes(c.conversationType) &&
        !(userRole === 'guest' && c.conversationType === 'deal')
      );

      // For landlord: aggressively filter out self-conversations
      if (userRole === 'landlord') {
        const currentId = user?._id ? String(user._id) : (user?.id ? String(user.id) : null);
        propertyConvs = propertyConvs.filter(c => {
          const hid = c.hostId ? String(c.hostId) : null;
          const gid = c.guestId ? String(c.guestId) : null;
          // Remove any conv where hostId === guestId (type-insensitive)
          if (hid && gid && hid === gid) return false;
          // For deal convs: landlord is hostId — if guestId is also the landlord, it's stale
          if (c.conversationType === 'deal' && currentId && gid === currentId) return false;
          // Also filter by name: if the "other person" name matches current user's name, skip it
          if (c.conversationType === 'deal') {
            const otherName = c.guestName || '';
            const myName = user?.name || user?.fullName || '';
            if (myName && otherName && otherName === myName) return false;
          }
          return true;
        });
      }

      let allConvs = propertyConvs;

      // For hosts: also fetch deal conversations where they are the guestId (enquirer)
      if (userRole === 'host') {
        try {
          const guestData = await messagesAPI.getConversations(token, 'guest');
          const dealConvs = guestData.filter(c => c.conversationType === 'deal');
          // Merge, avoiding duplicates
          const existingIds = new Set(propertyConvs.map(c => c._id));
          allConvs = [...propertyConvs, ...dealConvs.filter(c => !existingIds.has(c._id))];
        } catch {}
      }

      setConversations(allConvs);
      setFilteredConversations(allConvs);

      // Auto-select conversation if ID was passed
      if (autoSelectConversationId && !hasAutoSelected.current) {
        const target = allConvs.find(c => c._id === autoSelectConversationId);
        if (target) {
          setSelectedConversation(target);
          hasAutoSelected.current = true;
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, userRole, autoSelectConversationId]);

  const fetchProperties = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await propertyAPI.getHostProperties(token);
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, [getAuthToken]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversation(conversationId, token);
      setMessages(data.messages || []);
      // Opening a conversation marks messages as read on backend — notify parent
      if (onUnreadChange) onUnreadChange();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [getAuthToken, onUnreadChange]);

  // Polling fallback — refetch messages every 5s when WebSocket is not connected
  useEffect(() => {
    if (isConnected) return; // WebSocket working, no need to poll
    if (!selectedConversation?._id) return;

    const pollInterval = setInterval(() => {
      fetchMessages(selectedConversation._id);
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [isConnected, selectedConversation?._id, fetchMessages]);

  // Also poll conversation list periodically when WebSocket is disconnected
  useEffect(() => {
    if (isConnected) return;

    const pollInterval = setInterval(() => {
      fetchConversations();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [isConnected, fetchConversations]);

  // Reset auto-select when conversationId prop changes
  useEffect(() => {
    if (autoSelectConversationId) {
      hasAutoSelected.current = false;
      fetchConversations();
    }
  }, [autoSelectConversationId, fetchConversations]);

  useEffect(() => {
    fetchConversations();
    if (userRole === 'host') {
      fetchProperties();
    }
  }, [userRole, fetchConversations, fetchProperties]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    applyFilters();
  }, [conversations, searchQuery, filterUnread, filterTripStage, filterProperty, categoryFilter]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper: get the unread count for this user on a conversation
  const getConvUnread = (conv) => {
    // R2R host is guestId in deal conversations — their unread is unreadGuest
    if (conv.conversationType === 'deal' && userRole !== 'landlord') return conv.unreadGuest || 0;
    return (userRole === 'host' || userRole === 'landlord') ? (conv.unreadHost || 0) : (conv.unreadGuest || 0);
  };

  // Helper: get the "other person" name for a conversation
  const getOtherPersonName = (conv) => {
    if (conv.conversationType === 'deal' && userRole === 'host') return conv.hostName || 'Landlord';
    if (conv.conversationType === 'deal' && userRole === 'landlord') return conv.guestName || 'Host';
    if (userRole === 'host') return conv.guestName || 'Guest';
    if (userRole === 'provider') return conv.hostName || 'Host';
    return conv.hostName || 'Host';
  };

  const applyFilters = () => {
    let filtered = [...conversations];

    // Category filter (host only)
    if (userRole === 'host' && categoryFilter !== 'all') {
      if (categoryFilter === 'landlords') {
        filtered = filtered.filter(c => c.conversationType === 'deal');
      } else if (categoryFilter === 'guests') {
        filtered = filtered.filter(c => c.conversationType !== 'deal' && c.participantType !== 'vendor');
      } else if (categoryFilter === 'vendors') {
        filtered = filtered.filter(c => c.participantType === 'vendor');
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv =>
        conv.guestName?.toLowerCase().includes(query) ||
        conv.hostName?.toLowerCase().includes(query) ||
        conv.propertyTitle?.toLowerCase().includes(query) ||
        conv.lastMessage?.toLowerCase().includes(query)
      );
    }

    // Unread filter
    if (filterUnread) {
      filtered = filtered.filter(conv => getConvUnread(conv) > 0);
    }

    // Property filter (host only)
    if (filterProperty !== 'all') {
      filtered = filtered.filter(conv => conv.propertyId === filterProperty);
    }

    // Trip stage filter — conversations have tripStage from backend
    if (filterTripStage !== 'all') {
      filtered = filtered.filter(conv => {
        if (!conv.tripStage) return false;
        return conv.tripStage === filterTripStage;
      });
    }

    setFilteredConversations(filtered);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const token = getAuthToken();
      const message = await messagesAPI.sendMessage({
        conversationId: selectedConversation._id,
        content: newMessage.trim()
      }, token);

      setMessages(prev => [...prev, message]);
      setNewMessage('');
      // Refocus textarea so Enter key works immediately on next message
      setTimeout(() => textareaRef.current?.focus(), 0);

      // Update conversation's last message
      setConversations(prev => prev.map(conv =>
        conv._id === selectedConversation._id
          ? { ...conv, lastMessage: newMessage.trim(), lastMessageAt: new Date().toISOString() }
          : conv
      ));
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send message';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
      // If blocked by backend (ride ended or resolved), remove the optimistic message
      if (error.response?.status === 403) {
        setMessages(prev => prev.filter(m => m._id !== 'optimistic'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendPhoto = async (blob, metadata) => {
    if (!selectedConversation) return;
    setShowCamera(false);
    setSendingPhoto(true);
    try {
      const token = getAuthToken();
      const messageType = (userRole === 'host' || userRole === 'provider') ? 'evidence_photo' : 'evidence_photo';
      const message = await messagesAPI.sendPhotoMessage(
        selectedConversation._id, blob, messageType, metadata, token
      );
      setMessages(prev => [...prev, message]);
      setConversations(prev => prev.map(conv =>
        conv._id === selectedConversation._id
          ? { ...conv, lastMessage: message.content, lastMessageAt: new Date().toISOString() }
          : conv
      ));
      toast({ title: 'Photo sent', description: 'Your evidence photo has been sent.' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send photo';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    } finally {
      setSendingPhoto(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterUnread(false);
    setFilterTripStage('all');
    setFilterProperty('all');
    setCategoryFilter('all');
  };

  const hasActiveFilters = searchQuery || filterUnread || filterTripStage !== 'all' || filterProperty !== 'all' || categoryFilter !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[400px] max-h-[650px] bg-white rounded-lg border overflow-hidden">
      {/* Conversations List */}
      <div className={`w-full md:w-1/3 border-r ${selectedConversation ? 'hidden md:block' : ''}`}>
        {/* Search and Filter Header */}
        <div className="p-4 border-b bg-gray-50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-[#1F3A68]">Messages</h3>
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} title={isConnected ? 'Live' : 'Reconnecting...'} />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={hasActiveFilters ? 'text-[#1F3A68]' : ''}
            >
              <Filter size={16} />
            </Button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category Tabs — host only */}
          {userRole === 'host' && (
            <div className="flex gap-1 pt-1">
              {[
                { key: 'all', label: 'All' },
                { key: 'guests', label: 'Guests' },
                { key: 'landlords', label: 'Landlords' },
                { key: 'vendors', label: 'Vendors' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setCategoryFilter(tab.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === tab.key
                      ? 'bg-[#1F3A68] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={filterUnread ? 'default' : 'outline'}
                  onClick={() => setFilterUnread(!filterUnread)}
                  className={filterUnread ? 'bg-[#1F3A68]' : ''}
                >
                  Unread
                </Button>
                <select
                  value={filterTripStage}
                  onChange={(e) => setFilterTripStage(e.target.value)}
                  className="h-8 px-2 border rounded text-sm"
                >
                  <option value="all">All Trips</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="in-stay">In-Stay</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              {userRole === 'host' && properties.length > 0 && (
                <select
                  value={filterProperty}
                  onChange={(e) => setFilterProperty(e.target.value)}
                  className="w-full h-8 px-2 border rounded text-sm"
                >
                  <option value="all">All Properties</option>
                  {properties.map(p => (
                    <option key={p._id} value={p._id}>{p.title}</option>
                  ))}
                </select>
              )}
              
              {hasActiveFilters && (
                <Button variant="link" size="sm" onClick={clearFilters} className="text-gray-500">
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
        
        <div className="overflow-y-auto h-[calc(100%-140px)]">
          {filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
              {hasActiveFilters ? (
                <>
                  <p>No messages match your filters</p>
                  <Button variant="link" onClick={clearFilters}>Clear filters</Button>
                </>
              ) : (
                <>
                  <p>No conversations yet</p>
                  <p className="text-sm mt-2">Messages with {userRole === 'host' ? 'guests' : 'hosts'} will appear here</p>
                </>
              )}
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv._id}
                onClick={() => setSelectedConversation(conv)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedConversation?._id === conv._id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 ${
                    conv.conversationType === 'deal' ? 'bg-amber-600' : 'bg-[#1F3A68]'
                  }`}>
                    <User size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 truncate">
                        {getOtherPersonName(conv)}
                      </span>
                      {conv.lastMessageAt && (
                        <span className="text-xs text-gray-500">
                          {formatDate(conv.lastMessageAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm text-gray-600 truncate">{conv.propertyTitle}</p>
                      {conv.conversationType === 'deal' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 bg-amber-100 text-amber-700">
                          Deal
                        </span>
                      )}
                      {conv.tripStage && conv.tripStage !== 'inquiry' && conv.conversationType !== 'deal' && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          conv.tripStage === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                          conv.tripStage === 'in-stay' ? 'bg-green-100 text-green-700' :
                          conv.tripStage === 'completed' ? 'bg-gray-100 text-gray-600' :
                          conv.tripStage === 'cancelled' ? 'bg-red-100 text-red-600' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {conv.tripStage === 'in-stay' ? 'In-Stay' : conv.tripStage.charAt(0).toUpperCase() + conv.tripStage.slice(1)}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <p className="text-sm text-gray-500 truncate mt-1">{conv.lastMessage}</p>
                    )}
                  </div>
                  {getConvUnread(conv) > 0 && (
                    <div className="w-5 h-5 bg-[#CB3B3B] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">{getConvUnread(conv)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSelectedConversation(null)}
              >
                <ArrowLeft size={20} />
              </Button>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
                selectedConversation.conversationType === 'deal' ? 'bg-amber-600' : 'bg-[#1F3A68]'
              }`}>
                <User size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold">{getOtherPersonName(selectedConversation)}</h4>
                  {selectedConversation.conversationType === 'deal' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                      Landlord
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{selectedConversation.propertyTitle}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  // For provider role, match by senderId since provider uses guestId slot
                  // For deal conversations viewed by host: host is guestId, so senderRole is 'guest'
                  const currentUserId = user?._id || user?.id;
                  const isOwn = userRole === 'provider'
                    ? msg.senderId === currentUserId || msg.senderRole === 'provider'
                    : userRole === 'landlord'
                    ? msg.senderId === currentUserId
                    : selectedConversation.conversationType === 'deal' && userRole === 'host'
                    ? msg.senderId === currentUserId || msg.senderRole === 'guest'
                    : msg.senderRole === userRole;
                  const showDate = index === 0 ||
                    formatDate(msg.createdAt) !== formatDate(messages[index - 1]?.createdAt);
                  const isPhoto = msg.messageType === 'photo' || msg.messageType === 'evidence_photo';
                  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

                  return (
                    <React.Fragment key={msg._id}>
                      {showDate && (
                        <div className="text-center">
                          <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {formatDate(msg.createdAt)}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] ${
                          isOwn
                            ? 'bg-[#1F3A68] text-white rounded-l-lg rounded-tr-lg'
                            : 'bg-gray-100 text-gray-900 rounded-r-lg rounded-tl-lg'
                        } px-4 py-2`}>
                          {isPhoto ? (
                            <div>
                              {msg.messageType === 'evidence_photo' && (
                                <div className={`flex items-center gap-1 mb-1 text-xs font-semibold ${isOwn ? 'text-yellow-300' : 'text-yellow-600'}`}>
                                  <Shield className="w-3 h-3" /> Evidence Photo
                                </div>
                              )}
                              <div className="relative cursor-pointer group" onClick={() => setFullscreenPhoto(`${API_URL}${msg.photoUrl}`)}>
                                <img
                                  src={`${API_URL}${msg.photoUrl}`}
                                  alt="Photo message"
                                  className="rounded-lg max-w-full max-h-64 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <a
                                  href={`${API_URL}${msg.photoUrl}`}
                                  download={`photo-${msg._id || Date.now()}.jpg`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute top-1 right-1 p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                  onClick={e => e.stopPropagation()}
                                  title="Download photo"
                                >
                                  <Download className="w-3.5 h-3.5 text-[#1F3A68]" />
                                </a>
                              </div>
                              {msg.photoMetadata?.capturedAt && (
                                <div className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                                  Captured: {new Date(msg.photoMetadata.capturedAt).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          )}
                          <div className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                            {formatTime(msg.createdAt)}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Evidence prompt for guests when host has sent evidence photos */}
            {userRole === 'guest' && messages.some(m => m.messageType === 'evidence_photo' && m.senderRole === 'host') &&
             !messages.some(m => m.messageType === 'evidence_photo' && m.senderRole === 'guest') && (
              <div className="px-4 py-2 border-t bg-blue-50 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-700">The host has shared evidence photos. We recommend you also take photos to protect yourself.</p>
                </div>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" onClick={() => setShowCamera(true)}>
                  <Camera className="w-4 h-4 mr-1" /> Take Photo
                </Button>
              </div>
            )}

            {/* Typing Indicator */}
            {typingUsers[selectedConversation._id] && (
              <div className="px-4 py-1 text-sm text-gray-500 italic animate-pulse">
                {typingUsers[selectedConversation._id].senderName} is typing...
              </div>
            )}

            {/* Resolve button for guest on lost_item conversations */}
            {selectedConversation.conversationType === 'lost_item' && userRole === 'guest' && !selectedConversation.resolvedAt && (
              <div className="px-4 py-2 border-t bg-green-50 flex items-center justify-between">
                <p className="text-sm text-green-700">Has your lost item been returned?</p>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={async () => {
                    try {
                      const token = getAuthToken();
                      await messagesAPI.resolveLostItem(selectedConversation._id, token);
                      setSelectedConversation(prev => ({ ...prev, resolvedAt: new Date().toISOString() }));
                      setConversations(prev => prev.map(c =>
                        c._id === selectedConversation._id ? { ...c, resolvedAt: new Date().toISOString() } : c
                      ));
                      toast({ title: '✅ Resolved', description: 'Lost item report marked as resolved.' });
                    } catch (err) {
                      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to resolve', variant: 'destructive' });
                    }
                  }}
                >
                  ✅ Mark as Resolved
                </Button>
              </div>
            )}

            {/* Message Input */}
            {selectedConversation?.resolvedAt ? (
              <div className="p-3 border-t bg-green-50 text-center">
                <p className="text-sm text-green-700 font-medium">✅ This lost item report has been resolved.</p>
                <p className="text-xs text-green-600 mt-1">Messages are view-only.</p>
              </div>
            ) : selectedConversation?.lockedAt ? (
              <div className="p-3 border-t bg-amber-50 text-center">
                <p className="text-sm text-amber-700 font-medium">🔒 This conversation was locked after delivery completion.</p>
                <p className="text-xs text-amber-600 mt-1">Messages are view-only for record-keeping purposes.</p>
              </div>
            ) : (
            <div className="p-3 border-t bg-gray-50">
              {sendingPhoto && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                  Sending photo...
                </div>
              )}
              <div className="flex gap-2 items-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 flex-shrink-0 text-gray-500 hover:text-[#1F3A68]"
                  onClick={() => setShowCamera(true)}
                  title="Take evidence photo"
                >
                  <Camera size={20} />
                </Button>
                <EmojiPicker
                  onSelect={(emoji) => {
                    const ta = textareaRef.current;
                    if (ta) {
                      const start = ta.selectionStart || newMessage.length;
                      const end = ta.selectionEnd || newMessage.length;
                      const updated = newMessage.slice(0, start) + emoji + newMessage.slice(end);
                      setNewMessage(updated);
                      // Move cursor after emoji
                      setTimeout(() => {
                        ta.focus();
                        const pos = start + emoji.length;
                        ta.setSelectionRange(pos, pos);
                      }, 0);
                    } else {
                      setNewMessage(prev => prev + emoji);
                    }
                  }}
                />
                <Textarea
                  ref={textareaRef}
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    if (selectedConversation?._id && e.target.value.trim()) {
                      sendTypingIndicator(selectedConversation._id, true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="flex-1 min-h-[40px] max-h-[120px] resize-none"
                  rows={1}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#1F3A68] h-10"
                >
                  <Send size={20} />
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Enter to send · Shift+Enter for new line</p>
            </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Live Camera Capture Overlay */}
      {showCamera && (
        <LivePhotoCapture
          role={userRole}
          onCapture={handleSendPhoto}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Fullscreen Photo Lightbox */}
      {fullscreenPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setFullscreenPhoto(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setFullscreenPhoto(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          <img src={fullscreenPhoto} alt="Full size" className="max-w-[95vw] max-h-[95vh] object-contain" />
        </div>
      )}
    </div>
  );
};

export default MessagingPanel;
